import clsx from 'clsx'
import { ComponentPropsWithoutRef, CSSProperties, forwardRef, ReactNode } from 'react'

type Social = 'google' | 'facebook' | 'apple' | 'x'
type SocialButtonTheme = 'brand' | 'color' | 'gray'
type SocialButtonSize = 'md' | 'lg'

interface SocialButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
    social: Social
    theme?: SocialButtonTheme
    size?: SocialButtonSize
    iconOnly?: boolean
    label?: ReactNode
}

// Monochrome marks — fill="currentColor" (theme drives the colour via text-* / style color).
const Facebook = (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true" focusable="false">
        <path
            fill="currentColor"
            d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.3882 22.954 10.125 23.8542V15.4688H7.07812V12H10.125V9.35625C10.125 6.34875 11.9166 4.6875 14.6576 4.6875C15.9701 4.6875 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.34 7.875 13.875 8.80008 13.875 9.75V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z"
        />
    </svg>
)

const Apple = (
    <svg viewBox="0 0 18.531 22" className="h-6 w-6" aria-hidden="true" focusable="false">
        <path
            fill="currentColor"
            d="M18.1426 17.1447C17.8099 17.9133 17.4161 18.6208 16.9598 19.2713C16.3379 20.158 15.8286 20.7719 15.4362 21.1127C14.8278 21.6722 14.176 21.9587 13.478 21.975C12.9769 21.975 12.3726 21.8324 11.6691 21.5432C10.9634 21.2553 10.3148 21.1127 9.72179 21.1127C9.09983 21.1127 8.43279 21.2553 7.71931 21.5432C7.00474 21.8324 6.42909 21.9832 5.98897 21.9981C5.31962 22.0266 4.65244 21.7319 3.98649 21.1127C3.56144 20.742 3.02979 20.1064 2.3929 19.2061C1.70956 18.2446 1.14777 17.1297 0.707644 15.8587C0.236289 14.4857 0 13.1563 0 11.8692C0 10.3948 0.318582 9.12321 0.956698 8.0576C1.4582 7.20166 2.12538 6.52647 2.9604 6.03081C3.79542 5.53514 4.69766 5.28256 5.6693 5.2664C6.20095 5.2664 6.89814 5.43085 7.76453 5.75405C8.62848 6.07834 9.18321 6.24279 9.42643 6.24279C9.60826 6.24279 10.2245 6.0505 11.2692 5.66714C12.2571 5.31162 13.0909 5.16441 13.774 5.2224C15.6249 5.37178 17.0155 6.10142 17.9403 7.41595C16.2849 8.41896 15.466 9.82379 15.4823 11.626C15.4973 13.0297 16.0065 14.1979 17.0074 15.1254C17.4609 15.5558 17.9674 15.8885 18.531 16.1248C18.4088 16.4793 18.2798 16.8188 18.1426 17.1447ZM13.8976 0.440125C13.8976 1.54038 13.4956 2.56768 12.6944 3.51854C11.7275 4.64892 10.558 5.30211 9.28981 5.19904C9.27365 5.06705 9.26428 4.92812 9.26428 4.78214C9.26428 3.7259 9.72409 2.59552 10.5406 1.67127C10.9483 1.20331 11.4668 0.814209 12.0955 0.503814C12.7229 0.19805 13.3164 0.0289566 13.8745 0C13.8908 0.147086 13.8976 0.294182 13.8976 0.440111Z"
        />
    </svg>
)

const X = (
    <svg viewBox="0 0 21.937 21" className="h-6 w-6" aria-hidden="true" focusable="false">
        <path
            fill="currentColor"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.7341 21L9.4369 13.4496L2.8055 21H0L8.19222 11.6751L0 0H7.20295L12.1955 7.11615L18.4509 0H21.2564L13.4443 8.89301L21.937 21H14.7341ZM17.8583 18.8714H15.9695L4.01706 2.12864H5.9061L10.6932 8.83256L11.521 9.99587L17.8583 18.8714Z"
        />
    </svg>
)

// Multicolour mark — composed from the Figma vector fragments at their exact 24×24 offsets.
// The literal brand hex is permitted by allowsBrandArtwork (paint-value only).
const Google = (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true" focusable="false">
        <g transform="translate(1.5168, 0)">
            <path
                fill="#EA4335"
                d="M10.7235 4.74966C12.4343 4.7232 14.0878 5.36697 15.3268 6.54867L18.7529 3.12262C16.5835 1.0855 13.7042 -0.034466 10.7235 0.000808666C6.18629 0.000808666 2.03711 2.55822 0 6.61481L3.98604 9.70575C4.93404 6.86173 7.59287 4.74966 10.7235 4.74966Z"
            />
        </g>
        <g transform="translate(0.24, 6.6144)">
            <path
                fill="#FBBC04"
                d="M5.26254 7.68547C4.75988 6.19511 4.75988 4.5813 5.26254 3.09094V0H1.2765C-0.425501 3.39078 -0.425501 7.38563 1.2765 10.7764L5.26254 7.68547Z"
            />
        </g>
        <g transform="translate(1.5168, 14.2992)">
            <path
                fill="#34A853"
                d="M10.7235 9.70053C13.96 9.70053 16.6893 8.63788 18.6779 6.8036L14.811 3.80525C13.7351 4.5372 12.3461 4.95168 10.7279 4.95168C7.59728 4.95168 4.94286 2.83961 3.99045 0H0V3.09094C2.03711 7.14312 6.1863 9.70053 10.7235 9.70053Z"
            />
        </g>
        <g transform="translate(12.24, 9.8376)">
            <path
                fill="#4285F4"
                d="M11.526 2.43836C11.526 1.62263 11.4599 0.802499 11.3188 0H0V4.62098H6.48173C6.21276 6.11133 5.34853 7.42973 4.08305 8.2675V11.2658H7.95004C10.2209 9.17582 11.526 6.08929 11.526 2.43836Z"
            />
        </g>
    </svg>
)

const SOCIAL_LOGOS: Record<Social, ReactNode> = {
    google: Google,
    facebook: Facebook,
    apple: Apple,
    x: X,
}

const DEFAULT_LABELS: Record<Social, string> = {
    google: 'Sign in with Google',
    facebook: 'Sign in with Facebook',
    apple: 'Sign in with Apple',
    x: 'Sign in with X',
}

interface SizeClasses {
    withLabel: string
    iconOnly: string
    icon: string
}

const SIZE_CLASSES: Record<SocialButtonSize, SizeClasses> = {
    md: {
        withLabel: 'h-[38px] gap-spacing-md px-3.5 text-sm',
        iconOnly: 'size-10',
        icon: 'size-4',
    },
    lg: {
        withLabel: 'h-10 gap-2.5 px-spacing-xl text-md',
        iconOnly: 'size-11',
        icon: 'size-5',
    },
}

const LIGHT_SURFACE =
    'ring-1 ring-inset ring-border-primary bg-background-bg-primary text-text-secondary hover:bg-background-bg-primary_hover hover:text-text-secondary_hover'

const SURFACE_CLASSES: Record<Social, Record<SocialButtonTheme, string>> = {
    google: {
        brand: LIGHT_SURFACE,
        color: LIGHT_SURFACE,
        gray: LIGHT_SURFACE,
    },
    facebook: {
        brand: 'text-text-white',
        color: LIGHT_SURFACE,
        gray: LIGHT_SURFACE,
    },
    apple: {
        brand: 'bg-base-black text-text-white',
        color: LIGHT_SURFACE,
        gray: LIGHT_SURFACE,
    },
    x: {
        brand: 'bg-base-black text-text-white',
        color: LIGHT_SURFACE,
        gray: LIGHT_SURFACE,
    },
}

const GRAY_LOGO =
    'text-foreground-fg-quaternary group-hover:text-foreground-fg-quaternary_hover group-disabled:text-foreground-fg-quaternary'

const LOGO_CLASSES: Record<Social, Record<SocialButtonTheme, string>> = {
    google: {
        brand: '',
        color: '',
        gray: '',
    },
    facebook: {
        brand: 'text-foreground-fg-white',
        color: '',
        gray: GRAY_LOGO,
    },
    apple: {
        brand: 'text-foreground-fg-white',
        color: 'text-base-black',
        gray: GRAY_LOGO,
    },
    x: {
        brand: 'text-foreground-fg-white',
        color: 'text-base-black',
        gray: GRAY_LOGO,
    },
}

const SocialButton = forwardRef<HTMLButtonElement, SocialButtonProps>(
    (
        {
            social,
            theme = 'brand',
            size = 'lg',
            iconOnly = false,
            label,
            className,
            style,
            type = 'button',
            ...props
        },
        ref,
    ) => {
        const defaultLabel = DEFAULT_LABELS[social]
        const displayLabel = label ?? defaultLabel
        const accessibleName = props['aria-label'] || defaultLabel
        const sizeClasses = SIZE_CLASSES[size]
        const isFacebook = social === 'facebook'
        const isFacebookBrand = isFacebook && theme === 'brand'
        const isFacebookColor = isFacebook && theme === 'color'

        const getButtonStyle = (): CSSProperties | undefined => {
            if (isFacebookBrand) {
                return { backgroundColor: '#1877F2', ...style }
            }
            return style
        }

        const getLogoStyle = (): CSSProperties | undefined => {
            if (isFacebookColor) {
                return { color: '#1877F2' }
            }
            return undefined
        }

        return (
            <button
                ref={ref}
                type={type}
                className={clsx(
                    'group relative inline-flex shrink-0 items-center justify-center overflow-hidden',
                    'rounded-radius-md font-body font-semibold shadow-xs-skeuomorphic transition-colors',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    iconOnly ? sizeClasses.iconOnly : sizeClasses.withLabel,
                    SURFACE_CLASSES[social][theme],
                    className,
                )}
                style={getButtonStyle()}
                aria-label={iconOnly ? accessibleName : undefined}
                {...props}>
                {isFacebookBrand && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 bg-alpha-black-10 opacity-0 transition-opacity group-hover:opacity-100 group-disabled:opacity-0"
                    />
                )}
                <span
                    className={clsx(
                        'relative inline-flex shrink-0 [&>svg]:size-full',
                        sizeClasses.icon,
                        LOGO_CLASSES[social][theme],
                    )}
                    style={getLogoStyle()}>
                    {SOCIAL_LOGOS[social]}
                </span>
                {!iconOnly && <span className="relative truncate">{displayLabel}</span>}
            </button>
        )
    },
)

SocialButton.displayName = 'SocialButton'

export {
    SocialButton,
    type SocialButtonProps,
    type Social,
    type SocialButtonTheme,
    type SocialButtonSize,
}
